import React from 'react'
import Accordion from 'react-bootstrap/Accordion'
import { Link } from 'react-router-dom'
import HeaderSix from '../components/header/HeaderSix'
import FooterSix from '../components/footer/FooterSix'
import ScrollAnimation from '../components/motion/ScrollAnimation'

function CybersecurityService() {
    const cards = [
        {
            num: '01',
            title: 'Network Perimeter Defense',
            text: 'Advanced firewall technology with deep packet inspection, real-time intrusion detection and prevention, network segmentation that contains damage if something gets through, and secure, encrypted access for remote teams. Your perimeter is hardened.',
        },
        {
            num: '02',
            title: 'Endpoint Threat Detection & Response',
            text: 'Behavioral analysis running on every endpoint detects threats that signature-based tools miss entirely: fileless attacks, living-off-the-land techniques, and zero-day exploits. Compromised devices are automatically isolated before the damage spreads.',
        },
        {
            num: '03',
            title: 'Email Security & Anti-Phishing',
            text: 'Advanced threat protection sandboxes suspicious attachments before they are delivered. Artificial intelligence analyzes sender behavior and embedded links in real time. Simulated phishing campaigns train your team to recognize attacks before they click.',
        },
        {
            num: '04',
            title: 'Zero Trust Architecture',
            text: 'No user or device is trusted by default, inside or outside your network. Every access request is verified. Least-privilege access enforced. Administrative credentials are locked down with session monitoring and just-in-time access controls.',
        },
        {
            num: '05',
            title: '24/7 Security Operations',
            text: 'Our security operations team monitors your environment around the clock: detecting, investigating and responding to incidents in real time. Critical threats can be contained in under 15 minutes. Nothing goes unnoticed overnight.',
        },
        {
            num: '06',
            title: 'Identity & Access Management',
            text: 'Multi-factor authentication that stops credential-based attacks cold. Centralized authentication management across all systems. Privileged access controls with full audit trails so you always know who accessed what and when.',
        },
        {
            num: '07',
            title: 'Data Protection & Backup',
            text: 'Immutable backups ransomware cannot encrypt or delete. Data loss prevention that stops exfiltration before it leaves your network. Quarterly restore testing that proves your recovery plan works before you actually need it.',
        },
        {
            num: '08',
            title: 'Penetration Testing & Vulnerability Management',
            text: 'We find your weaknesses before attackers do using the same techniques real threat actors use. Full technical report with risk-scored findings and a clear remediation roadmap delivered after every engagement. Weekly automated scanning between tests.',
        },
    ]

    const whoFor = [
        {
            title: 'Small businesses',
            text: 'Automated attacks continuously scan the entire internet. Your size is not a defense; it’s an invitation. We deploy core protections fast, prioritized by your actual risk profile, at a cost that makes sense for where you are.',
        },
        {
            title: 'Mid-Size Organizations',
            text: "Large enough to have valuable data. Small enough that attackers assume you’re not fully protected. Around-the-clock monitoring, vulnerability management, and a security awareness program that actually changes employee behavior.",
        },
        {
            title: 'Enterprise & Multi-Location',
            text: 'Complex environments need integrated security, not a stack of disconnected tools creating alert fatigue. Advanced threat hunting, red team exercises, and a security operations function that augments or replaces your internal team.',
        },
        {
            title: 'Healthcare Organizations',
            text: 'Patient data. Zero-downtime requirements. Regulatory exposure on both the security and privacy sides. We build healthcare security programs that protect everything without disrupting care delivery.',
        },
        {
            title: 'Financial & Legal Firms',
            text: 'Client confidentiality is the product. One breach doesn’t just cost money, it ends relationships that took years to build. Security programs built around the compliance requirements and sensitivity levels these environments demand.',
        },
    ]

    const faqs = [
        {
            q: 'What is managed cybersecurity?',
            a: "Managed cybersecurity is the outsourced, ongoing protection of a business’s digital environment, including 24/7 monitoring, threat detection, incident response and security management handled by a dedicated external security team.",
        },
        {
            q: 'What is Zero Trust security architecture?',
            a: 'Zero Trust is a security model that requires verification for every user and device attempting to access any system, regardless of whether they are inside or outside the network perimeter. No one is trusted by default.',
        },
        {
            q: 'How quickly can a cybersecurity breach be contained?',
            a: 'With proper monitoring and incident response in place, critical security incidents can be contained in under 15 minutes. Without monitoring, the average business discovers a breach 200 days after it occurs.',
        },
        {
            q: 'Do small businesses really need managed cybersecurity?',
            a: 'Yes. Forty-three percent of cyberattacks specifically target small and mid-size businesses because attackers assume defenses are weaker. The average total cost of an attack on a small business exceeds $1,000,000.',
        },
    ]

    return (
        <div className="index-six service-page service-page--cybersecurity">
            <HeaderSix />
            <main>
                <ScrollAnimation direction="fade" delay={0.03} duration={0.38}>
                    <div className="about-page-breadcrumb">
                        <div className="container">
                            <h1 className="about-page-breadcrumb__title">Cybersecurity Services</h1>
                        </div>
                    </div>
                </ScrollAnimation>

                <ScrollAnimation direction="up" delay={0.06} duration={0.5}>
                    <section className="td-section rts-section-gap td-service-hero">
                        <div className="container">
                            <div className="td-service-hero__panel">
                                <div className="row align-items-center g-4 g-lg-5">
                                    <div className="col-lg-6">
                                        <div className="td-service-hero__kicker">Cybersecurity Services</div>
                                        <h2 className="td-service-hero__title">
                                            By the Time You Know You’ve Been Breached, It’s Already Too Late
                                        </h2>
                                        <p className="td-service-hero__lead">
                                            Enterprise-grade managed cybersecurity for businesses of every size, across every
                                            industry because attackers don’t wait for you to be ready and neither should your
                                            defenses.
                                        </p>
                                        <div className="button-area">
                                            <Link to="/contactus" className="rts-btn btn-primary">
                                                Schedule Your Free Consultation
                                            </Link>
                                        </div>
                                    </div>
                                    <div className="col-lg-6">
                                        <div className="td-service-hero__visual">
                                            <img
                                                src="/assets/images/about/cyber.jpeg"
                                                alt="Service overview"
                                                loading="lazy"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>

                <ScrollAnimation direction="left" delay={0.08} duration={0.52}>
                    <section className="td-section rts-section-gap">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-10 col-xl-9">
                                    <div className="td-section-head td-section-head--left">
                                        <h2 className="td-section-title">
                                            The Attack Isn’t Coming. For Most Businesses, It’s Already Happened.
                                        </h2>
                                        <div className="td-title-rule" aria-hidden="true" />
                                    </div>
                                    <p className="disc">
                                        The average business discovers a breach 200 days after it occurs. Two hundred days of
                                        an attacker sitting inside your systems. Reading your emails. Mapping your network.
                                        Watching your operations. Waiting for the right moment to deploy ransomware,
                                        exfiltrate data or simply sell access to someone else.
                                    </p>
                                    <p className="disc">
                                        You probably have antivirus software. You probably have a basic firewall. So did most
                                        of the businesses that got hit last year.
                                    </p>
                                    <p className="disc">
                                        Modern attacks don’t trigger antivirus alerts. They use your own tools against you.
                                        They came through a single phishing email, clicked by one employee on a Tuesday
                                        afternoon. They move laterally through your network while everyone is focused on
                                        their actual jobs.
                                    </p>
                                    <p className="disc">
                                        The question isn’t whether your business is a target. Every business with data worth
                                        stealing is a target. The question is whether your defenses will catch it or whether
                                        you’ll find out 200 days from now.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>

                <ScrollAnimation direction="up" delay={0.06} duration={0.48}>
                    <section className="td-section rts-section-gap">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-10 col-xl-9">
                                    <div className="td-section-head text-center mb-5">
                                        <p className="td-section-label">Full-Spectrum Security Protection</p>
                                        <h2 className="td-section-title">Every Layer Protected. Every Hour of the Day.</h2>
                                        <div className="td-title-rule td-title-rule--center mx-auto" aria-hidden="true" />
                                    </div>
                                </div>
                            </div>
                            <div className="row g-4">
                                {cards.map((c) => (
                                    <div key={c.num} className="col-lg-6">
                                        <div className="pillar-card h-100">
                                            <div className="pillar-card__header">
                                                <div className="pillar-card__head-main">
                                                    <div className="pillar-heading">
                                                        <span className="pillar-number">{c.num}</span>
                                                        <h4 className="pillar-title">{c.title}</h4>
                                                    </div>
                                                </div>
                                            </div>
                                            <p className="pillar-text">{c.text}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>

                <ScrollAnimation direction="right" delay={0.08} duration={0.52}>
                    <section className="td-section rts-section-gap">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-12">
                                    <div className="td-section-head td-section-head--left">
                                        <h2 className="td-section-title">No Industry Is Exempt. No Size Is Too Small to Target.</h2>
                                        <div className="td-title-rule" aria-hidden="true" />
                                    </div>
                                    <div className="row g-4 mt-1">
                                        {whoFor.map((w) => (
                                            <div key={w.title} className="col-md-6">
                                                <div className="service-quad-card h-100">
                                                    <h3 className="service-quad-card__title">{w.title}</h3>
                                                    <p className="service-quad-card__text">{w.text}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>

                <ScrollAnimation direction="fade" delay={0.04} duration={0.42}>
                    <section className="td-section rts-section-gap">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-10 col-xl-9 text-center">
                                    <h2 className="td-section-title">If You’re Under Attack Right Now — Call Us Immediately</h2>
                                    <div className="td-title-rule td-title-rule--center mx-auto" aria-hidden="true" />
                                    <p className="disc mt-3">
                                        Active security incidents require immediate action. Every minute of delay is lateral
                                        movement, data exfiltration or ransomware spreading to the next system.
                                    </p>
                                    <p className="disc">
                                        Our incident response team activates within minutes. Remote triage begins
                                        immediately. We contain, investigate, eradicate, and recover with full documentation
                                        for regulatory reporting requirements.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>

                <ScrollAnimation direction="up" delay={0.06} duration={0.48}>
                    <section className="faq-techdigit td-section td-section--faq rts-faq-section rts-section-gap">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-10 col-xl-9">
                                    <div className="faq-techdigit-inner">
                                        <div className="td-section-head text-center mb-5">
                                            <p className="td-section-label">FAQ</p>
                                            <h2 className="td-section-title">Questions People Ask</h2>
                                            <div className="td-title-rule td-title-rule--center mx-auto" aria-hidden="true" />
                                        </div>
                                        <div className="faq-accordion-area">
                                            <Accordion defaultActiveKey="0">
                                                {faqs.map((faq, i) => (
                                                    <Accordion.Item key={i} eventKey={String(i)}>
                                                        <Accordion.Header>{faq.q}</Accordion.Header>
                                                        <Accordion.Body>{faq.a}</Accordion.Body>
                                                    </Accordion.Item>
                                                ))}
                                            </Accordion>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>

                <ScrollAnimation direction="down" delay={0.05} duration={0.44}>
                    <section className="td-section rts-section-gap">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-10 col-xl-9 text-center">
                                    <h2 className="td-section-title">
                                        Find Out Where Your Defenses Are Weakest — Before Someone Else Does
                                    </h2>
                                    <div className="td-title-rule td-title-rule--center mx-auto" aria-hidden="true" />
                                    <p className="disc mt-3">
                                        One conversation. No obligation. We’ll talk through your current environment, the
                                        threats most relevant to your industry, and what a security program tailored to your
                                        business would look like.
                                    </p>
                                    <div className="button-area mt-4">
                                        <Link to="/contactus" className="rts-btn btn-primary">
                                            Schedule Your Free Consultation
                                        </Link>
                                    </div>
                                    <p className="disc mt-3">
                                        info@techdigitsolutions.com
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>
            </main>
            <FooterSix />
        </div>
    )
}

export default CybersecurityService

